import { Fragment, useEffect, useState } from "react";
import OnlineOrderValuesModel from "../../../../services/models/formModels/onlineOrderValues.model";
import CustomText from "../../../atomic/CustomText";
import DetailRenderer from "../DetailRenderer";
import axiosInstance from "../../../../services/api";
import { useOktaAuth } from "@okta/okta-react";
import suggestiveInputHelpers from "../../../../services/utils/helpers/suggestiveInputHelpers";
import { optionType } from "../../../organism/CustomSelect";
import ClinicianInformationReview from "./ClinicianInformationReview";

function BillingInformationReview({
  values,
}: {
  values: OnlineOrderValuesModel;
}) {
  const [icdCodes, setIcdCodes] = useState<optionType[]>([]);
  const [insurances, setInsurances] = useState<optionType[]>([]);
  const token = useOktaAuth()?.authState?.accessToken?.accessToken;

  function fetchPrimaryInsurance() {
    axiosInstance
      .get(`v1/primary-insurance`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.data) {
          setInsurances(
            response.data.data
              .sort(suggestiveInputHelpers.sortByAlphabeticalOrder)
              .map((item: any) => ({
                label: `${item.name}`,
                value: item.id,
              }))
          );
        }
      });
  }
  function fetchIcdCodes() {
    axiosInstance
      .get(`v1/test-type/${values.testTypeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.data) {
          setIcdCodes(
            response.data.data
              .sort(suggestiveInputHelpers.sortByAlphabeticalOrder)
              .map((item: any) => ({
                label: `${item.value} ${item.name}`,
                value: item.id,
              }))
          );
        }
      });
  }
  useEffect(() => {
    if (values.testTypeId) {
      fetchIcdCodes();
      fetchPrimaryInsurance();
    }
  }, []);

  const ICDCodeName = icdCodes?.find?.(
    (icdCode: optionType) => icdCode.value === values.billingInfo.icdCodeId
  )?.label;
  
  // Fallback to icdCodeDetails from backend if lookup fails
  const ICDCodeNameWithFallback = ICDCodeName ?? 
    (values.icdCodeDetails 
      ? `${values.icdCodeDetails.value} ${values.icdCodeDetails.name}` 
      : values.billingInfo.icdCodeId?.toString() ?? "");
  
  const PrimaryInsuranceName = insurances?.find?.(
    (insurance: optionType) =>
      insurance.value === values.billingInfo.primaryInsuranceId
  )?.label;


  return (
    <Fragment>
      <div id="billing info & medicare">
        <CustomText
          text="Billing Information"
          fontWeight="font-medium"
          className="reviewCardHeading my-5"
        />
        <div className="reviewCard">
          {values.testTypeId === 8 ? (
            <DetailRenderer
              data={{
                primaryDiagnosedICD10: ICDCodeNameWithFallback ?? "",
                additionalDiagnosedICD10: values.additionalCodes,
                primaryInsurance: values.billingInfo.primaryInsuranceId?.toString() === '19' ? 'Enter new insurance' : PrimaryInsuranceName,
                insuranceCompanyName: values.billingInfo.insuranceCompanyName,
                policyNumber: values.billingInfo.policyNumber,
                phone: values.billingInfo.phone,
              }}
              groupFields={[
                "primaryDiagnosedICD10",
                "additionalDiagnosedICD10",
                "primaryInsurance",
                "insuranceCompanyName",
                "phone",
                "policyNumber",
              ]}
              hiddenFields={[]}
              MainHeading="Billing Information"
            />
          ) : (
            <DetailRenderer
              data={{
                icd10: ICDCodeNameWithFallback,
                primaryInsurance: values.billingInfo.primaryInsuranceId?.toString() === '19' ? 'Enter new insurance' : PrimaryInsuranceName,
                insuranceCompanyName: values.billingInfo.insuranceCompanyName,
                policyNumber: values.billingInfo.policyNumber,
                phone: values.billingInfo.phone,
                typeOfFacility: values.billingInfo.typeOfFacility,
                dateOfDischarge: values.billingInfo.dateOfDischarge,
              }}
              groupFields={[
                "icd10",
                "primaryInsurance",
                "insuranceCompanyName",
                "phone",
                "policyNumber",
                "typeOfFacility",
                "dateOfDischarge",
              ]}
              hiddenFields={[]}
              MainHeading="Billing Information"
            />
          )}
        </div>
      </div>
      <ClinicianInformationReview values={values} />
    </Fragment>
  );
}

export default BillingInformationReview;
